import db from "../db.js";
import { findByProp, getNewImageId, getNewDrawingId } from "./images.js";
import { getNestedProperty } from "../lib/helpers.js";

export function getLocalInspectionsInStatus(status) {
	return db.inspections
		.where({ status: status })
		.toArray()
		.catch(a => console.log(a));
}

export function updateInspection(changeObj, dontSync) {
	return db.inspections.put(changeObj).then(inspKey => {
		if (!dontSync) {
			window.sync.postMessage({
				name: "inspectionUpdated",
				value: { inspectionId: changeObj._key }
			});
		}
		return inspKey;
	});
}

export function inspection(key) {
	if (!db.inspections || !key) return Promise.resolve(null);
	return db.inspections.get(key).catch(a => console.log(a, key));
}

export async function deleteCopiedInspection(i) {
	let result = await db.transaction("rw", db.inspections, db.images, db.drawings, async () => {
		const inspectionId = i._key;
		const imgIds = (await db.images.where({ inspectionId }).toArray()).map(i => i.id);
		const drwIds = (await db.drawings.where({ inspectionId }).toArray()).map(d => d.id);

		await db.images.bulkDelete(imgIds);
		await db.drawings.bulkDelete(drwIds);

		let insp = {
			_key: i._key,
			_id: i._id,
			_rev: i._rev,
			type: { name: i.type.name },
			name: "Deleted(" + i.name + ")",
			status: "deleted",
			deleted: true,
			customerId: i.customerId,
			customerName: i.customerName,
			date: i.date,
			inspector: i.inspector,
			projectName: i.projectName,
			copiedFrom: i.copiedFrom
		};
		await db.inspections.put(insp);
		
		return { inspectionId, imgIds, drwIds };
	});

	window.sync.postMessage({
		name: 'inspectionCopyDeleted',
		value: {
			inspectionId: result.inspectionId,
			imageIds: result.imgIds,
			drawingIds: result.drwIds
		}
	});
	return result.insp;
}

function getInspectionCopyId(oldKey) {
	if (oldKey.startsWith('copy_')) {
		oldKey = oldKey.split('_')[1];
	}
	let newKey = 'copy_' + oldKey + '_' + new Date().getTime();
	return newKey;
}

export async function copyInspection(i) {
	const oldKey = i._key;
	const newKey = getInspectionCopyId(oldKey);
	let inspCopy = JSON.parse(JSON.stringify(i));

	if (inspCopy.unilabSample != undefined) {
		await makeChangesForUnilabInspection(inspCopy);
	} else {
		inspCopy.name = inspCopy.name + ' Copy ' + new Date().toLocaleString();
	}

	inspCopy.copiedFrom = oldKey;
	inspCopy._key = newKey;

	delete inspCopy._id;
	delete inspCopy._rev;

	let r = await db.transaction("rw", db.inspections, db.images, db.drawings, async () => {
		let imgs = await db.images.where({ inspectionId: oldKey }).toArray();
		let drws = await db.drawings.where({ inspectionId: oldKey }).toArray();
		
		if (inspCopy.type._inspCopyModule) {
			let eval2 = eval; //this is workaround for rollup (https://github.com/rollup/rollup/wiki/Troubleshooting#avoiding-eval)
			let copyFunc = eval2("(function copyModule(currInsp, currImgs, currDrws) { " + inspCopy.type._inspCopyModule + "\n}\n)");
			if (typeof copyFunc === 'function') {
				let copyResult = copyFunc(inspCopy, imgs, drws);
				if (copyResult) {
					inspCopy = copyResult.insp;
					imgs = copyResult.imgs;
					drws = copyResult.drws;
				}
			}
		}
		
		if (imgs && imgs.length) {
			const contextIndex = findByProp(inspCopy, "connected_images");
			for (const img of imgs) {
				const oldImgId = img.id;
				const newImgId = await getNewImageId();
				img.id = newImgId;
				img.inspectionId = newKey;

				delete img._key;
				delete img._id;
				delete img._rev;

				await db.images.put(img);

				const ctx = contextIndex[oldImgId];
				const contextObj = getNestedProperty(inspCopy, ctx);
				contextObj.connected_images = contextObj.connected_images || {};
				delete contextObj.connected_images[oldImgId];

				if (inspCopy.cover_image_id != undefined && inspCopy.cover_image_id == oldImgId) {
					inspCopy.cover_image_id = newImgId;
				}

				contextObj.connected_images[newImgId] = newImgId;
			}
		}

		if (drws && drws.length) {
			for (const drw of drws) {
				drw.id = await getNewDrawingId();
				drw.inspectionId = newKey;
				delete drw._key;
				delete drw._id;
				delete drw._rev;
				_updateRpsDrawingData(i, inspCopy, drw);
			}
			await db.drawings.bulkPut(drws);
		}

		await db.inspections.put(inspCopy);

		return inspCopy;
	})
		.catch((error) => {
			throw error;
		});

	window.sync.postMessage({
		name: 'inspectionCopied_initial',
		value: { inspectionId: inspCopy._key }
	});

	return r;
}

function _updateRpsDrawingData(origInsp, copyInsp, drawing) {
	if (!origInsp ||
        !origInsp.inspectieDetails ||
        !origInsp.inspectieDetails.Inspectie_details ||
        origInsp.inspectieDetails.Inspectie_details.rPSMonsternummer == undefined ||
        !copyInsp ||
        !copyInsp.inspectieDetails ||
        !copyInsp.inspectieDetails.Inspectie_details ||
        copyInsp.inspectieDetails.Inspectie_details.rPSMonsternummer == undefined) {
		return;
	}

	const drwStr = drawing.drawing;
	if (drwStr && drwStr.indexOf('DrawLegendModule') > -1) {
		const origNum = origInsp.inspectieDetails.Inspectie_details.rPSMonsternummer;
		const oldRpsMonsternumber = `\"text\":\"Monsternummer: ${origNum}\",`;
		if (drwStr.indexOf(oldRpsMonsternumber) > -1) {
			const copyNum = copyInsp.inspectieDetails.Inspectie_details.rPSMonsternummer;
			const newRpsMonsternumber = `\"text\":\"Monsternummer: ${copyNum}\",`;
			drawing.drawing = drawing.drawing.replace(oldRpsMonsternumber, newRpsMonsternumber);
		}
	}
}

async function makeChangesForUnilabInspection(inspCopy) {
	const maxCopyIndex = await db.inspections
		.filter(i => i.unilabSample === inspCopy.unilabSample && i.copyIndex)
		.reverse()
		.sortBy('copyIndex')
		.then(r => r && r.length > 0 ? r[0].copyIndex : 0)
		.then(r => r || 0)
		.catch(a => {
			console.log(a);
			return 0
		});

	inspCopy.copyIndex = maxCopyIndex + 1;

	const adjustSuffix = (str, index) => {
		index = (index < 10) ? '0' + index : index;
		const s = str.split('_');
		if (s.length > 1) {
			s[s.length - 1] = index;
			return s.join('_');
		}
		return (str + '_' + index);
	};

	inspCopy.name = adjustSuffix(inspCopy.name, inspCopy.copyIndex);

	if (inspCopy.inspectieDetails == undefined) {
		inspCopy.inspectieDetails = {};
	}
	if (inspCopy.inspectieDetails.Inspectie_details == undefined) {
		inspCopy.inspectieDetails.Inspectie_details = {};
	}
	let monsterNummer;
	if (inspCopy.inspectieDetails.Inspectie_details.rPSMonsternummer) {
		monsterNummer = inspCopy.inspectieDetails.Inspectie_details.rPSMonsternummer =
            adjustSuffix(inspCopy.inspectieDetails.Inspectie_details.rPSMonsternummer, inspCopy.copyIndex);
	}

	if (inspCopy.Kleefmonster) {
		inspCopy.Kleefmonster.Kleefmonster._.RPS_monsternummer = monsterNummer;
	}
	if (inspCopy.Goudfilter) {
		inspCopy.Goudfilter.Goudfilter._.RPS_monsternummer = monsterNummer;
	}
	if (inspCopy.Algemeen && inspCopy.Algemeen.Algemeen && inspCopy.Algemeen.Algemeen.RPS_monsternummer) {
		inspCopy.Algemeen.Algemeen.RPS_monsternummer = adjustSuffix(inspCopy.Algemeen.Algemeen.RPS_monsternummer, inspCopy.copyIndex);
	}
}
